import type { ComponentType, SVGProps } from "react";
import { SITE } from "../config";
import { usePageMeta } from "../seo";
import {
  LayersIcon,
  ShieldIcon,
  SparkleIcon,
  BoltIcon,
  GlobeIcon,
  HandshakeIcon,
  CodeIcon,
  MailIcon,
  StopwatchIcon,
  BuildingIcon,
} from "../components/Icons";
import { DotPattern, GridPattern } from "../components/Patterns";

const beliefs = [
  {
    Icon: BoltIcon,
    title: "Built around real support work.",
    body:
      "Zopping's support team uses ZopDesk every day. What slows them down is what we fix next.",
  },
  {
    Icon: ShieldIcon,
    title: "Your accounts, your credentials.",
    body:
      "Replies go out through your mailbox, WhatsApp number and Acefone account; AI runs on your own key. Credentials are encrypted, and you can disconnect them in Settings.",
  },
  {
    Icon: SparkleIcon,
    title: "AI that answers to an agent.",
    body:
      "AI drafts and summarizes when an agent asks. A person decides what's sent.",
  },
  {
    Icon: LayersIcon,
    title: "Independent services.",
    body:
      "A problem in AI assist or a store integration shouldn't stop anyone from replying. Each service deploys and fails on its own.",
  },
  {
    Icon: HandshakeIcon,
    title: "Say what's built.",
    body:
      "Every page on this site describes what works today. Things that are planned are labelled as planned.",
  },
  {
    Icon: CodeIcon,
    title: "Ship small, ship often.",
    body:
      "Small changes, released when they're ready, with one database migration per release.",
  },
];

const timeline = [
  {
    when: "April 2026",
    title: "First version",
    body: "Email tickets from forwarding and connected mailboxes, replies through your own SMTP, the Shopify sidebar, and AI drafts and summaries with your own OpenAI or Anthropic key.",
  },
  {
    when: "October 2026",
    title: "Beta, with Zopping's support team",
    body: "WhatsApp and Acefone phone channels, rules and auto-close, SLA targets, satisfaction surveys and reports, saved replies, custom fields, companies, the Zopping store sidebar, the API with scoped keys, webhooks, customer data lookup and ticket actions.",
  },
  {
    when: "Next",
    title: "On the roadmap",
    body: "Live chat, Messenger and Instagram, a customer portal and a knowledge base. No dates yet; we'll add them here when they're real.",
  },
];

const stats = [
  { value: "8", label: "Backend services" },
  { value: "3", label: "Channels: email, WhatsApp, phone" },
  { value: "Beta", label: "Free while it lasts" },
  { value: "1", label: "Migration per release" },
];

const principles = [
  { Icon: ShieldIcon, title: "Scoped by org", body: "Every tenant row carries an org id. Middleware checks it on each request and every store query filters by it." },
  { Icon: GlobeIcon, title: "Hosted in the US", body: "Google Cloud, United States. There's no choice of region yet." },
  { Icon: StopwatchIcon, title: "Boring in production", body: "Go, GoFr, MySQL and Redis for caching. Services talk over plain HTTP; there's no event bus." },
  { Icon: CodeIcon, title: "An API for the core", body: "Tickets, customers and companies over a REST API with scoped keys, plus signed webhooks." },
];

export default function About() {
  usePageMeta("/about");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <DotPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 right-0 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-4 pt-16 pb-10 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            About {SITE.productName}
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl"
            style={{ animationDelay: "60ms" }}
          >
            A helpdesk built by the team behind Zopping.
          </h1>
          <div
            className="mt-8 animate-fade-up space-y-5 text-lg leading-relaxed text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            <p>
              Online stores get the same few questions all day, across email,
              WhatsApp and the phone: where's my order, can I return this, why
              was I charged twice. Answering them well means seeing the
              conversation and the order together.
            </p>
            <p>
              {SITE.productName} puts every conversation on one ticket
              timeline, shows the customer's store orders beside it, and gives
              agents saved replies, rules and AI drafts from their own OpenAI
              or Anthropic key.
            </p>
            <p>
              We're the team behind Zopping, an e-commerce platform, and
              Zopping's own support team runs on {SITE.productName}. It's in
              beta and free while it is. If it looks useful, try it; if
              something's missing, tell us.
            </p>
          </div>
        </div>
      </section>


      <section className="border-y border-ink-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className="animate-fade-up rounded-xl border border-ink-200 bg-ink-50 p-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="text-3xl font-semibold tracking-tight text-ink-900">
                  {s.value}
                </p>
                <p className="mt-1 text-sm text-ink-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            What we believe
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            Six things that shape what we build.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {beliefs.map(({ title, body, Icon }, i) => (
            <div
              key={title}
              className="animate-fade-up rounded-xl border border-ink-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                <Icon width={20} height={20} />
              </div>
              <h3 className="mb-2 text-base font-semibold text-ink-900">
                {title}
              </h3>
              <p className="text-sm leading-relaxed text-ink-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative border-y border-ink-200 bg-white">
        <div className="pointer-events-none absolute inset-0 text-ink-200/50">
          <GridPattern />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              Timeline
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Where we've been. What's next.
            </h2>
          </div>
          <ol className="relative space-y-8 border-l-2 border-ink-200 pl-6 sm:pl-10">
            {timeline.map((t, i) => (
              <li
                key={t.when}
                className="animate-fade-up relative"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <span className="absolute -left-[34px] top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-ink-200 bg-white sm:-left-[46px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                    {t.when}
                  </span>
                </div>
                <h3 className="mt-1 text-lg font-semibold text-ink-900">
                  {t.title}
                </h3>
                <p className="mt-2 max-w-2xl text-sm text-ink-600">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              Engineering principles
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Boring where it counts.
            </h2>
            <p className="mt-4 text-ink-600">
              We pick well-understood tools so the system is easy to reason
              about when something goes wrong. The stack isn't the product.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {principles.map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className="animate-fade-up rounded-xl border border-ink-200 bg-ink-50 p-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-brand-700 ring-1 ring-ink-200">
                  <Icon width={18} height={18} />
                </div>
                <h3 className="mb-1 text-sm font-semibold text-ink-900">
                  {title}
                </h3>
                <p className="text-xs leading-relaxed text-ink-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <ContactCard
              Icon={MailIcon}
              label="Questions, help, feedback"
              value={SITE.contactEmail}
              href={`mailto:${SITE.contactEmail}`}
            />
            <ContactCard
              Icon={ShieldIcon}
              label="Security or privacy"
              value={SITE.contactEmail}
              href={`mailto:${SITE.contactEmail}?subject=Security`}
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-ink-200 bg-ink-50 p-10 text-center sm:p-14">
          <svg
            aria-hidden
            className="pointer-events-none absolute -top-10 -left-10 h-48 w-48 text-brand-500/20"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle cx="50" cy="50" r="40" stroke="currentColor" strokeDasharray="3 5" />
            <circle cx="50" cy="50" r="22" stroke="currentColor" strokeDasharray="2 4" />
          </svg>
          <div className="relative">
            <div className="mx-auto mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-brand-700 shadow-sm ring-1 ring-ink-200">
              <BuildingIcon width={22} height={22} />
            </div>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900">
              Try it with your own inbox.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-ink-600">
              Free during the beta. Sign in with Google and connect a channel.
            </p>
            <div className="mt-6">
              <a
                href={SITE.appUrl}
                className="rounded-md bg-gradient-to-br from-brand-600 to-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-sm shadow-brand-600/20 transition hover:from-brand-700 hover:to-violet-700 hover:shadow-md"
              >
                Start free
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactCard({
  Icon,
  label,
  value,
  href,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="group flex items-start gap-4 rounded-xl border border-ink-200 bg-white p-5 transition hover:border-brand-200 hover:shadow-sm"
    >
      <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100">
        <Icon width={18} height={18} />
      </span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">
          {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-ink-900 group-hover:text-brand-700">
          {value}
        </p>
      </div>
    </a>
  );
}
